import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { ServiceConstants } from "constant";
import { ContractService } from "services";
import { Bank, ContractOptionsResponse } from "types";

import { ContractDetails } from "./types";
import { formatContractDateForApi, parseContractDate, pickActiveContract } from "./utils";

const { REQUEST_CONTRACT_GET_ID, REQUEST_CONTRACT_OPTIONS_ID } = ServiceConstants;

export interface UseContractCoreReturn {
  contractId: number | undefined;
  contractDetails: ContractDetails | undefined;
  previousContractDetails: ContractDetails | undefined;
  contractOptions: ContractOptionsResponse | undefined;
  shippingMethod: string | null;
  scheduledPaymentDate: Date | null;
  banOnRefinancingUntil: Date | null;
  updateContractDetails: (updates: Partial<ContractDetails>) => void;
  updateContractField: (name: string, value: unknown) => void;
  handleShippingMethodChange: (value: string | null) => void;
  handleScheduledPaymentDateChange: (value: Date | null) => void;
  handleBanOnRefinancingUntilChange: (value: Date | null) => void;
}

export const useContractCore = (
  bankCreditRequestId: number,
  bank: Bank
): UseContractCoreReturn => {
  const [contractId, setContractId] = useState<number | undefined>();
  const [contractDetails, setContractDetails] = useState<ContractDetails | undefined>();
  const [previousContractDetails, setPreviousContractDetails] = useState<ContractDetails | undefined>();
  const [contractOptions, setContractOptions] = useState<ContractOptionsResponse | undefined>();
  const [shippingMethod, setShippingMethod] = useState<string | null>(null);

  const contractRequestKey = REQUEST_CONTRACT_GET_ID + "_" + bank;

  const contractResponse = useSelector((state: any) => state.service.object[contractRequestKey]);
  const contractOptionsResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_OPTIONS_ID]
  );

  // Reset when bank changes
  useEffect(() => {
    setShippingMethod(null);
    setContractId(undefined);
    setContractDetails(undefined);
    setPreviousContractDetails(undefined);
  }, [bank]);

  // Fetch contract options once on mount
  useEffect(() => {
    ContractService.getContractOptions();
  }, []);

  // Fetch contract when bank/bankCreditRequestId change
  useEffect(() => {
    if (bankCreditRequestId && bank) {
      ContractService.getContract(bankCreditRequestId, bank);
    }
  }, [bankCreditRequestId, bank]);

  // Handle contract options response
  useEffect(() => {
    if (contractOptionsResponse?.data) {
      setContractOptions(contractOptionsResponse.data as ContractOptionsResponse);
    }
  }, [contractOptionsResponse]);

  // Handle contract response
  useEffect(() => {
    if (!contractResponse?.data) return;

    const { contract, previousContract } = pickActiveContract(
      contractResponse.data as ContractDetails | ContractDetails[],
    );

    if (contract?.id) {
      setContractDetails(contract as ContractDetails);
      setContractId(contract.id);
      setShippingMethod((prev) => contract.shippingMethod || prev || null);
      setPreviousContractDetails(previousContract);
    } else {
      setContractDetails(undefined);
      setContractId(undefined);
      setPreviousContractDetails(undefined);
    }
  }, [contractResponse]);

  // Sync bank field on contract when it doesn't match
  useEffect(() => {
    if (contractId && contractDetails && contractDetails.bank !== bank) {
      setContractDetails((prev) => (prev ? { ...prev, bank } : prev));
      ContractService.contractUpdateMultipleProperties(contractId, { bank });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, bank]);

  // Auto-save shipping method when contract loads without one
  useEffect(() => {
    if (contractId && shippingMethod && !contractDetails?.shippingMethod) {
      ContractService.contractUpdateMultipleProperties(contractId, { shippingMethod });
      setContractDetails((prev) => (prev ? { ...prev, shippingMethod } : prev));
    }
  }, [contractDetails?.shippingMethod, contractId, shippingMethod]);

  const updateContractDetails = (updates: Partial<ContractDetails>) => {
    setContractDetails((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const updateContractField = (name: string, value: unknown) => {
    if (!contractId) return;
    ContractService.contractUpdateMultipleProperties(contractId, { [name]: value });
  };

  const handleShippingMethodChange = (value: string | null) => {
    if (!value) return;
    setShippingMethod(value);
    if (contractId) {
      ContractService.contractUpdateMultipleProperties(contractId, { shippingMethod: value });
    }
  };

  const handleScheduledPaymentDateChange = (value: Date | null) => {
    const nextValue = formatContractDateForApi(value);
    updateContractDetails({ scheduledPaymentDate: nextValue });
    updateContractField("scheduledPaymentDate", nextValue);
  };

  const handleBanOnRefinancingUntilChange = (value: Date | null) => {
    const nextValue = formatContractDateForApi(value);
    updateContractDetails({ banOnRefinancingUntil: nextValue });
    updateContractField("banOnRefinancingUntil", nextValue);
  };

  return {
    contractId,
    contractDetails,
    previousContractDetails,
    contractOptions,
    shippingMethod,
    scheduledPaymentDate: parseContractDate(contractDetails?.scheduledPaymentDate),
    banOnRefinancingUntil: parseContractDate(contractDetails?.banOnRefinancingUntil),
    updateContractDetails,
    updateContractField,
    handleShippingMethodChange,
    handleScheduledPaymentDateChange,
    handleBanOnRefinancingUntilChange,
  };
};
